import React from "react";
import { CustomCardWithCharts } from "../../../../SharedComponents/CustomCardWithCharts";
import { calculateAverages, groupBy, splitColumnValues } from "../../../../Utils/js-common";
import { ChartDataOption } from "../../../../SharedComponents/Interfaces/CustomCardWithCharts.interfaces";

const QualityTabCharts = (props: {
  loading: boolean;
  filteredData: any[];
  onSelectedChartFilter: React.Dispatch<
    React.SetStateAction<{
      filterKey: string;
      value: string;
    }>
  >;
  tenantId: any;
  tenantName: any;
  kustoLinks?: Record<string, string>;
}) => {
  // Properties
  const [metricFilters, setMetricFilters] = React.useState<{ [key: string]: any }>({});

  // Events
  const handleResetFilters = () => {
    setMetricFilters({});
    props.onSelectedChartFilter({
      filterKey: "",
      value: ""
    });
  };
  /**
   * onClick action for each datapoint in the chart
   */
  const handleDataPointClick = (option: ChartDataOption<any>) => {
    props.onSelectedChartFilter({
      filterKey: option.field,
      value: option.value
    });
    setMetricFilters((prev) => ({ ...prev, [option.title]: option.value }));
  };

  // Events
  const getGroupedCountData = (key: string, data: any[]) => {
    const filteredData = data.filter((item) => item[key]);
    const groupedData = groupBy(filteredData, key);

    return Object.keys(groupedData)?.map((item) => ({
      name: item,
      value: groupedData[item].reduce((sum: any, currentItem: { count: any }) => sum + currentItem.count, 0)
    }));
  };

  const groupByAverageCount = (xs: any[], key1: string) => {
    return xs.reduce((rv, x) => {
      if (!rv[x[key1]]) {
        rv[x[key1]] = { sum: 0, count: 0 };
      }
      rv[x[key1]].sum += parseInt(x["csatSum"]);
      rv[x[key1]].count += x["count"];
      return rv;
    }, {});
  };

  const staffingTypeDistribution = getGroupedCountData("staffingType", props.filteredData);
  const reopenCountDistribution = getGroupedCountData("reopenCount", props.filteredData);
  const transferCountDistribution = getGroupedCountData("transferCount", props.filteredData);

  const csatData = props.filteredData.filter((item) => item["csat"]);

  const csatSRDsitribution = calculateAverages(groupByAverageCount(csatData, "monthYear"));
  const csatCaseAgeDitribution = calculateAverages(groupByAverageCount(csatData, "caseAgeBucket"));
  const csatCRIDitribution = calculateAverages(
    groupByAverageCount(splitColumnValues(csatData, "relatedICM_Id", ","), "monthYear")
  );

  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: 20,
          rowGap: 20,
          margin: 8,
          justifyContent: "space-between",
          maxWidth: "calc(100% - 10px)"
        }}
      >
        <CustomCardWithCharts
          loading={props.loading}
          metricFilters={metricFilters}
          onDataPointClick={handleDataPointClick}
          onResetFilters={handleResetFilters}
          options={{
            chartOptions: [
              {
                id: 1,
                field: "monthYear",
                key: "product-view-csat-sr-trend-support-chart-by-monthYear",
                title: "CSAT Trend on SR Volume",
                type: "line",
                legendName: "CSAT",
                tooltipDescription:
                  "This represents the volume distribution of Service Requests based on the Customer Satisfaction Score.",
                groupedData: csatSRDsitribution,
                kustoLink: props?.kustoLinks?.SRDistribution
              },
              {
                id: 2,
                field: "caseAgeBucket",
                key: "product-view-csat-support-chart-by-caseAgeBucket",
                title: "CSAT Trend by SR Age Bucket",
                type: "horizontalbar",
                tooltipDescription:
                  "This represents the distribution of Service Requests by Customer Satisfaction Score and Case Age.",
                groupedData: csatCaseAgeDitribution,
                kustoLink: props?.kustoLinks?.CaseAgeDistribution
              },
              {
                id: 3,
                field: "monthYear",
                key: "product-view-csat-sr-cri-tren-support-chart-by-monthYear",
                title: "CSAT Trend By CRI Volume",
                type: "line",
                legendName: "CSAT",
                tooltipDescription:
                  "This represents the volume distribution of Customer Reported Incidents based on the Customer Satisfaction Score.",
                groupedData: csatCRIDitribution,
                kustoLink: props?.kustoLinks?.CRIDistribution
              },
              {
                id: 4,
                field: "staffingType",
                key: "product-view-sr-volume-support-chart-by-staffingType",
                title: "SR Volume Distribution by Staffing Type",
                type: "donut",
                tooltipDescription:
                  "This represents the volume distribution of Service Requests based on the assigned staffing type (e.g., FTE, Delivery Partner, etc.).",
                groupedData: staffingTypeDistribution,
                kustoLink: props?.kustoLinks?.StaffingType
              },
              {
                id: 5,
                field: "reopenCount",
                key: "product-view-sr-volume-support-chart-by-reopenCount",
                title: "SR Volume Distribution by Reopen Count",
                type: "donut",
                tooltipDescription:
                  "This represents the volume distribution of Service Requests based on the number of times a request was reopened on the same tenant.",
                groupedData: reopenCountDistribution,
                kustoLink: props?.kustoLinks?.ReopenCount
              },
              {
                id: 6,
                field: "transferCount",
                key: "product-view-sr-volume-support-chart-by-transferCount",
                title: "SR Volume Distribution by Transfer Count",
                type: "donut",
                tooltipDescription:
                  "This represents the volume distribution of Service Requests based on the number of times a request was transferred before reaching the correct team.",
                groupedData: transferCountDistribution,
                kustoLink: props?.kustoLinks?.TransferCount
              }
            ]
          }}
        />
      </div>
    </>
  );
};

export default QualityTabCharts;
